import React, { useEffect, useRef, useState } from "react";
import { Sparkles, Send, Loader2, X, Coins, Maximize2, Mic, Square, CalendarClock, Check, RotateCcw } from "lucide-react";
import { toast } from "sonner";
import api from "@/lib/api";

const QUICK_PROMPTS = [
  "What's scheduled to post today?",
  "Pause my 4pm post",
  "Post my latest blog to Facebook now",
  "Give me 3 content ideas for this week",
];

// Map intent keywords → a one-tap hand-off to the right tool.
const ACTIONS = [
  { tab: "image", label: "Open Image Studio", kw: /image|photo|graphic|picture|visual|thumbnail|logo/i },
  { tab: "empire", label: "Open Empire Builder", kw: /workflow|automat|funnel|\bdm\b|reply|sequence|trigger/i },
  { tab: "signals", label: "Open Local Signals", kw: /\bjob|event|propert|local|signal|scrape|listing/i },
  { tab: "calendar", label: "Open Calendar", kw: /schedule|calendar|plan|posting time|cadence/i },
  { tab: "connections", label: "Open Connections", kw: /connect|instagram|facebook|tiktok|youtube|account|link my/i },
];

const actionsFor = (text) => {
  const hits = ACTIONS.filter((a) => a.kw.test(text || ""));
  return hits.slice(0, 2);
};

// A floating, always-available Wingman launcher + compact chat.
// Shown on every dashboard tab except the full Wingman tab.
const FloatingWingman = ({ user, refreshUser, onOpenFull, onNavigate, pageLabel }) => {
  const [open, setOpen] = useState(false);
  const [messages, setMessages] = useState([]);
  const [draft, setDraft] = useState("");
  const [sending, setSending] = useState(false);
  const [threadId, setThreadId] = useState(() => {
    try { return localStorage.getItem("tam_thread_id") || null; } catch { return null; }
  });
  const [actions, setActions] = useState([]);
  const [pendingAction, setPendingAction] = useState(null);
  const [confirming, setConfirming] = useState(false);
  const [recording, setRecording] = useState(false);
  const [transcribing, setTranscribing] = useState(false);
  const scrollRef = useRef(null);
  const recRef = useRef(null);
  const chunksRef = useRef([]);

  const transcribe = async (blob) => {
    setTranscribing(true);
    try {
      const ext = blob.type.includes("mp4") ? "mp4" : "webm";
      const fd = new FormData();
      fd.append("file", blob, `voice.${ext}`);
      const { data } = await api.post("/wingman/transcribe", fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      if (data.text) setDraft((d) => (d ? d + " " : "") + data.text);
      else toast.info("Didn't catch that — try again.");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Couldn't transcribe that.");
    } finally {
      setTranscribing(false);
    }
  };

  const startRec = async () => {
    if (recording) return;
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      const mime = MediaRecorder.isTypeSupported("audio/webm")
        ? "audio/webm"
        : MediaRecorder.isTypeSupported("audio/mp4")
        ? "audio/mp4"
        : "";
      const mr = new MediaRecorder(stream, mime ? { mimeType: mime } : undefined);
      chunksRef.current = [];
      mr.ondataavailable = (e) => e.data.size && chunksRef.current.push(e.data);
      mr.onstop = async () => {
        stream.getTracks().forEach((t) => t.stop());
        const blob = new Blob(chunksRef.current, { type: mr.mimeType || "audio/webm" });
        if (blob.size > 0) await transcribe(blob);
      };
      mr.start();
      recRef.current = mr;
      setRecording(true);
    } catch {
      toast.error("Microphone access denied or unavailable.");
    }
  };

  const stopRec = () => {
    if (recRef.current && recording) {
      recRef.current.stop();
      setRecording(false);
    }
  };

  useEffect(() => {
    if (scrollRef.current) scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
  }, [messages, open, sending]);

  // Restore the ongoing conversation when the widget opens, so Tam "remembers".
  useEffect(() => {
    if (!open || !threadId || messages.length > 0) return;
    (async () => {
      try {
        const { data } = await api.get(`/wingman/threads/${threadId}`);
        if (Array.isArray(data) && data.length) {
          setMessages(data.map((m) => ({ role: m.role, content: m.content })));
        }
      } catch { /* start fresh if the thread is gone */ }
    })();
  }, [open, threadId]); // eslint-disable-line react-hooks/exhaustive-deps

  const newChat = () => {
    setMessages([]);
    setThreadId(null);
    setActions([]);
    setPendingAction(null);
    try { localStorage.removeItem("tam_thread_id"); } catch { /* ignore */ }
  };

  const confirmAction = async () => {
    if (!pendingAction) return;
    setConfirming(true);
    try {
      if (pendingAction.kind === "control") {
        const { data } = await api.post("/wingman/control", pendingAction.control);
        toast.success(data.message || "Done.");
        setMessages((m) => [...m, { role: "assistant", content: `✅ ${data.message || "Done"} — ${pendingAction.summary}` }]);
      } else {
        const { data } = await api.post("/wingman/confirm-action", {
          automation: pendingAction.automation,
        });
        toast.success("Scheduled! It'll run itself from now on.");
        setMessages((m) => [...m, { role: "assistant", content: `✅ Done — ${data.summary}` }]);
      }
      setPendingAction(null);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Couldn't do that.");
    } finally {
      setConfirming(false);
    }
  };

  const goto = (tab) => {
    setOpen(false);
    onNavigate?.(tab);
  };

  const send = async (override) => {
    const text = (override ?? draft).trim();
    if (!text || sending) return;
    if (user?.role !== "admin" && (user?.wingman_tokens || 0) < 5) {
      toast.error("Out of Tam tokens. Top up on the pricing page.");
      return;
    }
    setSending(true);
    setActions([]);
    setPendingAction(null);
    setDraft("");
    setMessages((m) => [...m, { role: "user", content: text }]);
    try {
      const { data } = await api.post("/wingman/message", {
        text,
        thread_id: threadId,
        context: `The user is on the "${pageLabel || "Dashboard"}" page. Their plan is ${user?.plan || "free"}.`,
      });
      setThreadId(data.thread_id);
      try { if (data.thread_id) localStorage.setItem("tam_thread_id", data.thread_id); } catch { /* ignore */ }
      setMessages((m) => [...m, { role: "assistant", content: data.reply }]);
      setActions(actionsFor(`${text} ${data.reply}`));
      setPendingAction(data.action || null);
      if (data.streak?.milestone_hit) {
        toast.success(
          `🔥 ${data.streak.milestone_hit}-day streak! +${data.streak.bonus_tokens} tokens.`
        );
      }
      refreshUser?.();
    } catch (e) {
      if (e?.response?.status === 401) {
        toast.error("You've been signed out. Please sign back in — your message is kept.");
      } else {
        toast.error(e?.response?.data?.detail || "Tam could not reply.");
      }
      setDraft(text);
      setMessages((m) => m.filter((x) => !(x.role === "user" && x.content === text)));
    } finally {
      setSending(false);
    }
  };

  return (
    <>
      {!open && (
        <button
          onClick={() => setOpen(true)}
          data-testid="floating-wingman-launcher"
          aria-label="Open Tam"
          className="fixed bottom-6 right-6 z-40 h-14 w-14 rounded-full bg-[#00F0FF] text-[#050B14] shadow-[0_0_30px_rgba(0,240,255,0.5)] flex items-center justify-center hover:scale-105 active:scale-95 transition-transform"
        >
          <Sparkles size={22} />
          <span className="absolute -top-1 -right-1 h-3 w-3 rounded-full bg-emerald-400 border-2 border-[#050B14] animate-pulse" />
        </button>
      )}

      {open && (
        <div
          data-testid="floating-wingman-panel"
          className="fixed bottom-6 right-6 z-40 w-[92vw] max-w-md h-[72vh] max-h-[600px] bg-[#0A192F] border border-cyan-400/30 rounded-lg shadow-2xl flex flex-col overflow-hidden cf-glow"
        >
          <header className="flex items-center justify-between px-4 py-3 border-b border-white/10 bg-[#050B14]">
            <div className="flex items-center gap-2">
              <Sparkles size={16} className="text-cyan-300" />
              <span className="font-heading font-bold text-white text-sm">Tam</span>
            </div>
            <div className="flex items-center gap-3">
              <span className="flex items-center gap-1 text-[10px] font-mono text-slate-400" data-testid="floating-wingman-tokens">
                <Coins size={12} className="text-cyan-300" />
                {user?.role === "admin" ? "∞" : (user?.wingman_tokens ?? 0)}
              </span>
              <button
                onClick={newChat}
                data-testid="floating-wingman-newchat"
                title="Start a new chat"
                className="text-slate-400 hover:text-cyan-300"
              >
                <RotateCcw size={14} />
              </button>
              <button
                onClick={() => onOpenFull?.()}
                data-testid="floating-wingman-expand"
                title="Open full Tam"
                className="text-slate-400 hover:text-cyan-300"
              >
                <Maximize2 size={15} />
              </button>
              <button
                onClick={() => setOpen(false)}
                data-testid="floating-wingman-close"
                title="Minimise"
                className="text-slate-400 hover:text-white"
              >
                <X size={16} />
              </button>
            </div>
          </header>

          <div ref={scrollRef} className="flex-1 overflow-y-auto p-3 space-y-2.5" data-testid="floating-wingman-messages">
            {messages.length === 0 && (
              <div className="text-center text-slate-500 text-sm pt-6">
                <p className="text-slate-300">Right, what we doing?</p>
                <p className="mt-1 text-[11px] font-mono text-slate-600">{user?.role === "admin" ? "Ask me anything · unlimited" : "Ask me anything · 5 tokens a reply"}</p>
                <div className="mt-4 space-y-2">
                  {QUICK_PROMPTS.map((q) => (
                    <button
                      key={q}
                      onClick={() => send(q)}
                      data-testid="floating-wingman-quick"
                      className="block w-full text-left px-3 py-2 text-xs bg-[#050B14] border border-white/10 hover:border-cyan-400/40 hover:text-cyan-200 text-slate-300 rounded-sm transition-colors"
                    >
                      {q}
                    </button>
                  ))}
                </div>
              </div>
            )}
            {messages.map((m, i) => (
              <div
                key={i}
                data-testid={`floating-wingman-msg-${m.role}-${i}`}
                className={[
                  "max-w-[88%] px-3 py-2 rounded-sm text-sm whitespace-pre-wrap",
                  m.role === "user"
                    ? "ml-auto bg-cyan-400/10 border border-cyan-400/30 text-white"
                    : "bg-[#050B14] border border-white/10 text-slate-200",
                ].join(" ")}
              >
                {m.content}
              </div>
            ))}
            {sending && (
              <div className="bg-[#050B14] border border-white/10 px-3 py-2 max-w-[88%] text-slate-500 text-sm">
                Tam is thinking…
              </div>
            )}
            {!sending && pendingAction && (
              <div
                className="p-3 rounded-sm border border-cyan-400/40 bg-cyan-400/5"
                data-testid="floating-wingman-action-confirm"
              >
                <p className="text-[10px] font-mono uppercase tracking-widest text-cyan-300 flex items-center gap-1">
                  <CalendarClock size={12} /> {pendingAction.kind === "control" ? "Confirm this change" : "Confirm this automation"}
                </p>
                <p className="text-sm text-white mt-1 capitalize">{pendingAction.summary}</p>
                <div className="flex gap-2 mt-2">
                  <button
                    onClick={confirmAction}
                    disabled={confirming}
                    data-testid="floating-wingman-action-confirm-btn"
                    className="inline-flex items-center gap-1 px-3 py-1.5 bg-[#00F0FF] text-[#050B14] font-bold rounded-sm text-[10px] uppercase tracking-wider hover:bg-[#33F3FF] disabled:opacity-50"
                  >
                    {confirming ? <Loader2 size={12} className="animate-spin" /> : <Check size={12} />} {pendingAction.kind === "control" ? "Confirm" : "Confirm & schedule"}
                  </button>
                  <button
                    onClick={() => setPendingAction(null)}
                    data-testid="floating-wingman-action-dismiss-btn"
                    className="px-3 py-1.5 border border-white/15 text-slate-400 hover:text-white rounded-sm text-[10px] uppercase tracking-wider"
                  >
                    Dismiss
                  </button>
                </div>
              </div>
            )}
            {!sending && actions.length > 0 && (
              <div className="flex flex-wrap gap-2 pt-1" data-testid="floating-wingman-actions">
                {actions.map((a) => (
                  <button
                    key={a.tab}
                    onClick={() => goto(a.tab)}
                    data-testid={`floating-wingman-action-${a.tab}`}
                    className="px-3 py-1.5 text-[10px] font-bold uppercase tracking-wider rounded-sm border border-cyan-400/40 text-cyan-300 hover:bg-cyan-400/10 flex items-center gap-1"
                  >
                    {a.label} →
                  </button>
                ))}
              </div>
            )}
          </div>

          <div className="p-3 border-t border-white/10 flex gap-2">
            <input
              value={draft}
              onChange={(e) => setDraft(e.target.value)}
              onKeyDown={(e) => e.key === "Enter" && send()}
              placeholder={recording ? "Listening… tap stop when done" : transcribing ? "Transcribing…" : "Ask Tam…"}
              disabled={recording || transcribing}
              data-testid="floating-wingman-input"
              className="flex-1 px-3 py-2 bg-[#050B14] border border-white/10 focus:border-cyan-400 outline-none text-white placeholder-slate-500 text-sm rounded-sm disabled:opacity-70"
            />
            <button
              onClick={recording ? stopRec : startRec}
              disabled={transcribing}
              data-testid="floating-wingman-mic"
              title={recording ? "Stop recording" : "Speak to Tam"}
              className={[
                "px-3 py-2 rounded-sm border disabled:opacity-60 flex items-center justify-center",
                recording
                  ? "bg-red-500/20 border-red-400/50 text-red-300 animate-pulse"
                  : "bg-[#050B14] border-white/10 text-slate-300 hover:text-cyan-300 hover:border-cyan-400/40",
              ].join(" ")}
            >
              {transcribing ? (
                <Loader2 size={16} className="animate-spin" />
              ) : recording ? (
                <Square size={16} />
              ) : (
                <Mic size={16} />
              )}
            </button>
            <button
              onClick={() => send()}
              disabled={sending || recording || transcribing || !draft.trim()}
              data-testid="floating-wingman-send"
              className="px-3 py-2 bg-[#00F0FF] text-[#050B14] font-bold rounded-sm hover:bg-[#33F3FF] disabled:opacity-60"
            >
              {sending ? <Loader2 size={16} className="animate-spin" /> : <Send size={16} />}
            </button>
          </div>
        </div>
      )}
    </>
  );
};

export default FloatingWingman;
